/**
 * Simple embedding utilities for text representation
 * Creates semantic 2D projections for visualization
 */

export interface TextChunk {
  text: string
  size: number
}

export interface Embedding2D {
  x: number
  y: number
  text: string
  id: string
}

/**
 * Generate simple embeddings from text chunks
 * Creates 128-dimensional vectors based on character frequencies and n-grams
 */
export function generateSimpleEmbeddings(chunks: TextChunk[]): number[][] {
  return chunks.map((chunk) => {
    const text = chunk.text.toLowerCase()
    const embedding = new Array(128).fill(0)

    // Character frequency features (0-25)
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i)
      if (charCode >= 97 && charCode <= 122) {
        // a-z
        embedding[charCode - 97] += 1
      }
    }

    // Bigram features (26-75)
    for (let i = 0; i < text.length - 1; i++) {
      const bigram = text.substring(i, i + 2)
      const hash = hashString(bigram) % 50
      embedding[26 + hash] += 1
    }

    // Length and punctuation features (76-127)
    embedding[76] = text.length / 100 // Normalized length
    embedding[77] = (text.match(/\s/g) || []).length / text.length // Space ratio
    embedding[78] = (text.match(/[.!?]/g) || []).length // Sentence endings
    embedding[79] = (text.match(/[,;:]/g) || []).length // Commas and semicolons
    embedding[80] = chunk.size / 10 // Importance score

    // Fill remaining with random variation based on text hash
    const seed = hashString(text)
    for (let i = 81; i < 128; i++) {
      embedding[i] = Math.sin(seed * i) * 0.5
    }

    // Normalize
    const magnitude = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0))
    return embedding.map((val) => (magnitude > 0 ? val / magnitude : 0))
  })
}

/**
 * Simple hash function for strings
 */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

/**
 * Project high-dimensional embeddings to 2D
 * Simplified version inspired by UMAP
 */
export function projectTo2D(embeddings: number[][]): [number, number][] {
  if (embeddings.length === 0) return []

  // Simple projection: use PCA-like approach
  // Take first and second principal components
  return embeddings.map((emb, i) => {
    // Average first half vs second half (simplified PCA)
    const x = emb.slice(0, 64).reduce((a, b) => a + b, 0) / 64
    const y = emb.slice(64).reduce((a, b) => a + b, 0) / 64

    // Add slight spread based on index to avoid exact overlaps
    const spread = 5
    const angle = (i / embeddings.length) * Math.PI * 2
    const xSpread = Math.cos(angle) * spread * Math.random()
    const ySpread = Math.sin(angle) * spread * Math.random()

    return [x * 20 + xSpread, y * 20 + ySpread]
  })
}

/**
 * Normalize 2D coordinates to canvas space
 */
export function normalizeToCanvas(
  embeddings2D: [number, number][],
  chunks: TextChunk[],
  width: number,
  height: number,
  padding = 40
): Embedding2D[] {
  if (embeddings2D.length === 0) return []

  // Find bounds
  let minX = Math.min(...embeddings2D.map((e) => e[0]))
  let maxX = Math.max(...embeddings2D.map((e) => e[0]))
  let minY = Math.min(...embeddings2D.map((e) => e[1]))
  let maxY = Math.max(...embeddings2D.map((e) => e[1]))

  const rangeX = maxX - minX || 1
  const rangeY = maxY - minY || 1

  // Normalize to canvas space
  return embeddings2D.map((emb, i) => {
    const x = padding + ((emb[0] - minX) / rangeX) * (width - 2 * padding)
    const y = padding + ((emb[1] - minY) / rangeY) * (height - 2 * padding)

    return {
      x,
      y,
      text: chunks[i].text,
      id: `chunk-${i}`,
    }
  })
}

/**
 * Complete pipeline: chunks -> embeddings -> 2D projection -> canvas coordinates
 */
export function embedAndProject(
  chunks: TextChunk[],
  width: number,
  height: number,
  padding = 40
): Embedding2D[] {
  const embeddings = generateSimpleEmbeddings(chunks)
  const embeddings2D = projectTo2D(embeddings)
  return normalizeToCanvas(embeddings2D, chunks, width, height, padding)
}
